import type { Employee } from '../../types/employee';
import type { RegulatoryRates } from '../../types/regulatoryRates';
import { isDailyWorkerPay } from '../../types/employee';
import { calculateFullWorkerInsurance } from './full-worker-insurance';
import type { DayInsuranceRatesSnapshot } from './types';

export const PENSION_HEALTH_MIN_WORK_DAYS = 8;

export interface DayInsuranceEstimate {
  dailyWage: number;
  employerEmployment: number;
  workerEmployment: number;
  employerIndustrial: number;
}

export function estimateDayInsurance(
  employee: Employee,
  rates: DayInsuranceRatesSnapshot,
): DayInsuranceEstimate | null {
  const dailyRate = employee.dailyRate ?? 0;
  if (!isDailyWorkerPay(employee) || dailyRate <= 0) return null;

  const wage = Math.round(dailyRate);
  const wci =
    (employee.industrialAccidentRate ?? 0) > 0
      ? employee.industrialAccidentRate!
      : rates.wciAverage;

  return {
    dailyWage: wage,
    employerEmployment: Math.round(wage * (rates.eiEmployer + rates.eiSafetyUnder150)),
    workerEmployment: Math.round(wage * rates.eiWorker),
    employerIndustrial: Math.round(wage * wci),
  };
}

export function workerDeductionFromNetPay(
  employee: Employee,
  workDays: number,
  totalSalary: number,
  estimate: DayInsuranceEstimate | null,
  fullRates: RegulatoryRates,
): number {
  if (!isDailyWorkerPay(employee) || workDays <= 0 || totalSalary <= 0) {
    return 0;
  }

  const workerEmployment = (estimate?.workerEmployment ?? 0) * workDays;

  if (employee.hasFourInsurance && workDays >= PENSION_HEALTH_MIN_WORK_DAYS) {
    return calculateFullWorkerInsurance(employee, totalSalary, fullRates);
  }

  return workerEmployment;
}

export function workDaysFromSalary(baseSalary: number, dailyRate: number): number {
  if (dailyRate <= 0) return 0;
  return Math.round(baseSalary / dailyRate);
}
